import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ, formatLongDate } from '@utils/dateUtils';
import { investigatedInstant, type IInvestigationReport } from '@apis/investigationReports';
import { RecentCardShell } from '../RecentReports/RecentCardShell';
import styles from '../RecentReports/RecentCardShell.module.css';

interface IDailyRouteRecentCardProps {
  submission: IInvestigationReport;
  siteId: string;
}

/**
 * Recent-list rendering for a Daily Route submission. Reads the typed
 * variant fields off the archived record (the registry guarantees this
 * card only receives `dailyRoute` submissions) and renders
 * `entityName - entityNick · investigatedDate`, mirroring trusstor's
 * `RecentInvestigationReportCard` entity row.
 */
export function DailyRouteRecentCard({ submission, siteId }: IDailyRouteRecentCardProps) {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ja' ? 'ja' : 'en';
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  const isTag = submission.entityType === 'tag';
  const entityName = isTag ? submission.tagName : submission.equipmentName;
  const entityNick = isTag ? submission.tagNick : undefined;
  const entityId = isTag ? submission.tagId : submission.equipmentId;
  // Suppress redundancy: when name and nick coincide (or nick falls back
  // to the bare id from the BFF), render only the more descriptive label.
  const showBoth = entityName && entityNick && entityName !== entityNick;
  const entityLabel =
    showBoth ? `${entityName} - ${entityNick}` : entityName || entityNick || entityId;
  const instant = investigatedInstant(submission);
  const analysedDate = instant ? formatLongDate(instant, lang, tz) : '';
  return (
    <RecentCardShell
      reportType="dailyRoute"
      reportId={submission.reportId}
      siteId={siteId}
      createdAtIso={submission.dateGeneratedIso}
      title={submission.title}
    >
      <span className={styles.subtitle}>{entityLabel}</span>
      {/* `investigatedTimestamp` is project-tz-noon ms (BFF-stamped), so
          formatting it in the working-site tz reliably yields the day the
          user picked. A legacy row that never stored one shows no date at
          all rather than a substituted one — the shell's top-right slot
          already carries the archive date, labelled as such. */}
      {analysedDate && (
        <>
          <span className={styles.divider} aria-hidden="true" />
          <span className={styles.subtitle}>{analysedDate}</span>
        </>
      )}
    </RecentCardShell>
  );
}
